import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx'
import { Accordion, AccordionSummary, AccordionDetails  } from '@mui/material';

import Checkbox from 'components/Checkbox/Checkbox'
import { ReactComponent as ExpandMore } from 'assets/icons/expand-more.svg'
import { AppConsentsListProps, IAccordionExpanIconProps, IAppConsentsProps as IAppSingleConsentProps } from './AppConsentProps'
import { appConsentsCheckOne, appConsentsToggleAll } from 'store/ConsentsSlice/ConsentsSlcie'
import { IAppConsentsModel } from 'service/SettingsService/SettingsServiceModels'
import { AppState } from 'store'
import { Environment } from 'enviroment/AppSettings'

import './AppConsent.scss'


/**
 * Komponent wyświetlajacy ikonkę strzałki w górę / w dół
*/
const AccordeonExpandIcon = (props: IAccordionExpanIconProps): JSX.Element | null => {

   if(props.details === undefined){
      return null
   }

   return (
      <ExpandMore className={clsx(props.hasError ? 'app-consent-expand-icon expand-icon-has-error' : 'app-consent-expand-icon')} />
   )
}

/**
 * Komponent wyświetlajacy listę wszystkich zgód, składającą się z pojedynczych zgód AppSingleConsent
*/
const ConsentsList = (props: AppConsentsListProps): JSX.Element | null => {

   if(!props.appConsents.length) {
      return null
   }
   
   return (
		<div className='app-consent-list-outer-wrapper'>
			{props.appConsents.map((consent: IAppConsentsModel) => {

				const isChecked = consent.isChecked
            const heading = consent.isRequired ? `<span>*</span>${consent.title}` : consent.title

            const handleOnClick = (e: Event) => {
               e.stopPropagation()
               props.dispatch(appConsentsCheckOne(consent.consentId))
            }

				return (
					<AppSingleConsent
						key={consent.consentId}
						heading={heading}
						details={consent.text.length? <p className='app-consent-list-consent-detail' dangerouslySetInnerHTML={{ __html: consent.text }} /> : undefined}
						isChecked={isChecked}
						handleCheckboxClick={(e: Event) => handleOnClick(e)}
                  hasError={consent.hasError}
					/>
				)
			})}
         <p className='app-consent-list-mandatory consent-text'><span>*</span>Zgody obligatoryjne</p>
			<p className='app-consent-list-consent-note'>
				Pamiętaj, że niezależnie od sposobu zaznaczenia, w każdym przypadku
				należy rozwinąć treść danego oświadczenia lub zgody i zapoznać się z
				nią (zaznaczając i przechodząc dalej potwierdzasz fakt zapoznania
				się z nią i jej akceptację). 
            <br/>
            <br/>
            Złożenie każdego z tych oświadczeń (udzielenie każdej ze zgód) jest dobrowolne.
            Bez zaznaczenia tych wskazanych jako „obligatoryjne” nie będzie jednak możliwe
            przejście na dalszy etap wnioskowania o pożyczkę.
            <br/>
            <br/>
            Masz prawo odwołania oświadczenia (zgody) w dowolnym momencie, przy czym 
            nie będzie to miało wpływu na zgodność z prawem przetwarzania Twoich 
            danych przed takim odwołaniem. W celu odwołania zgody udzielonej Paytree lub
				podmiotowi, który na jej podstawie udostępnia Paytree Twoje dane,
            możesz kontaktować się poprzez paytree.pl/kontakt 
			</p>
		</div>
	)
}

/**
 * Komponent pojedynczej zgody na liście, obsługujący mechanim pokazywania / ukrywania rozszerzonej treści zgody (accordeon element) 
*/
const AppSingleConsent = (props: IAppSingleConsentProps): JSX.Element => {

   const [isOpen, setIsOpen] = useState<boolean>(Boolean(props.isOpen))


   return (
		<Accordion
			disableGutters
			className={clsx('app-consent-outer-wrapper', props.hasError && 'app-consent-has-error')}
			onChange={() => setIsOpen(!isOpen)}
			expanded={isOpen}
		>
			<AccordionSummary
				expandIcon={<AccordeonExpandIcon hasError={props.hasError} details={props.details} isOnTop={props.isOnTop} />}
				classes={{ root: 'app-consent-summary-wrapper', content: 'app-consent-summary-inner-wrapper', }}
			>
				<div className='app-consent-heading-wrapper' id="app-consent-heading-wrapper">
					<Checkbox
						isChecked={props.isChecked}
						className={clsx('app-consent-checkbox', props.isOnTop && props.hasError && 'app-consent-checkbox-main-error' )}
						handleOnClick={props.handleCheckboxClick}
						hasError={props.hasError}
					/>
					<h4
						dangerouslySetInnerHTML={{ __html: props.heading }}
						className={
                     clsx(
                        'app-consent-heading', 
                        props.hasError && 'app-consents-main-error',
                        props.isOnTop && 'app-consent-heading-primary', 
                     )}
					/>
					{!props.isOnTop && props.hasError && (
						<b className='app-consent-error-message'>Zgoda obowiązkowa</b>
					)}
					<div className='app-consent-flex-breakline' />
					<p
						className='app-consent-aside-text'
						dangerouslySetInnerHTML={{ __html: props.aside || '' }}
						style={{gridArea: props.isOnTop ? 'app-consent-error-message' : 'app-consent-aside-text'}}
					/>
				</div>
			</AccordionSummary>
			<AccordionDetails 
            classes={{root: 'app-consent-details-root'}}
         >
            {props.details}
         </AccordionDetails>
		</Accordion>
	)
}

/**
 * Komponent zgód aplikacji wyświetlany na jednej ze stron formularza
*/
const AppConsent = (): JSX.Element | null => {

   const dispatch = useDispatch() 
   const appConsents = useSelector((state: AppState) => state.consentsState.appConsents)
   
   const [hasError, setHasError] = useState(false)
   const [isAllConsentsMarked, setIsAllConsentsMarked] = useState<boolean>(appConsents?.every((appConsent: IAppConsentsModel) => appConsent.isChecked))
   const [isChecked, setIsChecked] = useState<boolean>(false)

   const handleOnClick = (e: Event) => {
      e.preventDefault()
      const scrollDownElement = document.getElementById(Environment.formSubmitionButtonId) as HTMLButtonElement

      setTimeout(() => {
         if(!isChecked === true){
            scrollDownElement.scrollIntoView({ behavior:"smooth", block: "center" }) // Jeśli checkbox "zaznacz wszystkie zgody" został zaznaczony, scrollujemy do przycisku "Dalej"
         }
      }, 300) //Opóźnienie po to aby zaczekać aż animacja otwierania / zamykania dodatkowych zgód się wykona.

      setIsChecked(!isChecked)

      dispatch(appConsentsToggleAll(!isChecked))
   }

   useEffect(() => {
      setIsAllConsentsMarked(appConsents?.every((appConsent: IAppConsentsModel) => appConsent.isChecked))
      setHasError(appConsents?.some((appConsent: IAppConsentsModel) => appConsent.hasError))
   }, [ appConsents ])

   if(!appConsents.length){
      return null
   }

   return (
      <div className='app-consents-main-content-outer-wrapper'>
         <AppSingleConsent 
            isChecked={isAllConsentsMarked}
            isOpen={isChecked}
            isOnTop={true}
            hasError={hasError}
            heading='Zaznacz wszystkie oświadczenia'
            aside='Kliknij tu jeśli chcesz zaznaczyć wszystkie poniższe oświadczenia (w tym zgody).'
            details={<ConsentsList appConsents={appConsents} dispatch={dispatch} /> }
            handleCheckboxClick={(e: Event) => handleOnClick(e)}
         />
      </div>
   )
}

export default AppConsent